import React, { useState } from 'react';
import { TrendingDown, PhoneOff, DollarSign, ArrowRight } from 'lucide-react';
import { Slider } from './ui/slider';
import { Button } from './ui/button';

const RevenueCalculator = ({ onBookConsultation }) => {
  const [missedCallsPerWeek, setMissedCallsPerWeek] = useState(8);
  const [bookingValue, setBookingValue] = useState(150);

  // Calculate lost revenue
  const lostRevenuePerWeek = missedCallsPerWeek * bookingValue;
  const lostRevenuePerMonth = lostRevenuePerWeek * 4;
  const lostRevenuePerYear = lostRevenuePerWeek * 52;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      padding: window.innerWidth <= 768 ? '1.5rem 0.75rem' : '3rem 2rem',
      background: 'white',
      borderRadius: '24px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      {/* MOBILE-FIRST VERTICAL STACK */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        width: '100%',
        maxWidth: '100%'
      }}>
        {/* Header */}
        <div style={{ width: '100%', textAlign: 'center' }}>
          <div style={{ 
            width: '56px', 
            height: '56px', 
            background: '#fee2e2', 
            borderRadius: '16px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 1rem'
          }}>
            <PhoneOff style={{ width: '32px', height: '32px', color: '#ef4444' }} />
          </div>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '1.5rem' : '1.875rem',
            fontWeight: '700',
            marginBottom: '0.75rem',
            color: '#1a202c'
          }}>
            Calculate Your Lost Revenue
          </h3>
          <p style={{
            fontSize: '1rem',
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Every missed call is a missed opportunity. See how much revenue you could be losing.
          </p>
        </div>

        {/* Input 1: Missed Calls - VERTICAL STACK */}
        <div style={{
          width: '100%',
          maxWidth: '100%',
          background: '#f8fafc',
          padding: '1.5rem',
          borderRadius: '16px',
          boxSizing: 'border-box'
        }}>
          {/* Label ABOVE input */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PhoneOff style={{ width: '20px', height: '20px', color: '#ef4444' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e293b' }}>
                Missed Calls per Week
              </span>
            </div>
            <span style={{ 
              fontSize: '1.25rem', 
              fontWeight: '700', 
              color: '#2563eb' 
            }}>
              {missedCallsPerWeek}
            </span>
          </div>
          {/* Slider full width */}
          <Slider
            value={[missedCallsPerWeek]}
            onValueChange={(value) => setMissedCallsPerWeek(value[0])}
            min={1}
            max={50}
            step={1}
            style={{ width: '100%' }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.75rem',
            color: '#94a3b8',
            marginTop: '0.5rem'
          }}>
            <span>1 call</span>
            <span>50 calls</span>
          </div>
        </div>

        {/* Input 2: Booking Value - VERTICAL STACK */}
        <div style={{
          width: '100%',
          maxWidth: '100%',
          background: '#f8fafc',
          padding: '1.5rem',
          borderRadius: '16px',
          boxSizing: 'border-box'
        }}>
          {/* Label ABOVE input */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <DollarSign style={{ width: '20px', height: '20px', color: '#2563eb' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e293b' }}>
                Average Booking Value
              </span>
            </div>
            <span style={{ 
              fontSize: '1.25rem', 
              fontWeight: '700', 
              color: '#2563eb' 
            }}>
              {formatCurrency(bookingValue)}
            </span>
          </div>
          {/* Slider full width */}
          <Slider
            value={[bookingValue]}
            onValueChange={(value) => setBookingValue(value[0])}
            min={50}
            max={500}
            step={10}
            style={{ width: '100%' }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.75rem',
            color: '#94a3b8',
            marginTop: '0.5rem'
          }}>
            <span>£50</span>
            <span>£500</span>
          </div>
        </div>

        {/* Results BELOW inputs - VERTICAL STACK */}
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {/* Weekly */}
          <div style={{
            width: '100%',
            background: '#fef2f2',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '2px solid #fee2e2',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <TrendingDown style={{ width: '20px', height: '20px', color: '#ef4444' }} />
              <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>Weekly Lost Revenue</p>
            </div>
            <p style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#ef4444',
              margin: 0 
            }}>
              {formatCurrency(lostRevenuePerWeek)}
            </p>
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.5rem 0 0 0' }}>
              {missedCallsPerWeek} calls × {formatCurrency(bookingValue)}
            </p>
          </div>

          {/* Monthly */}
          <div style={{
            width: '100%',
            background: '#fefce8',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '2px solid #fef08a',
            boxSizing: 'border-box'
          }}>
            <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 0.5rem 0' }}>
              Monthly Lost Revenue
            </p>
            <p style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700', 
              color: '#eab308',
              margin: 0 
            }}>
              {formatCurrency(lostRevenuePerMonth)}
            </p>
          </div>

          {/* Yearly */}
          <div style={{
            width: '100%',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            padding: '1.5rem',
            borderRadius: '16px',
            color: 'white',
            boxSizing: 'border-box'
          }}>
            <p style={{ fontSize: '0.875rem', opacity: 0.9, margin: '0 0 0.5rem 0' }}>
              Yearly Lost Revenue
            </p>
            <p style={{ 
              fontSize: '2.25rem', 
              fontWeight: '700',
              margin: '0 0 1rem 0' 
            }}>
              {formatCurrency(lostRevenuePerYear)}
            </p>
            <Button 
              onClick={onBookConsultation}
              style={{
                width: '100%',
                background: 'white',
                color: '#ef4444',
                padding: '0.75rem 1.5rem',
                borderRadius: '12px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              <span>Stop Losing Revenue</span>
              <ArrowRight style={{ width: '20px', height: '20px', marginLeft: '0.5rem' }} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueCalculator;
